// THIS FILE IS COPIED FROM SRC/MIXINS TO FUNCTIONS/SRC/HELPERS. ONLY EDIT THIS FILE IN SRC/MIXINS.
// CHANGES IN FUNCTIONS/SRC/HELPERS WILL BE OVERWRITTEN.

export const globalFilterFields = ["name", "id", "mimbox_vers", "access", "company"];

const lastAliveTimeMB2 = 1000 * 60 * 60 * 1; // 1hour
const lastAliveTimeMB1 = 1000 * 60 * 60 * 2; // 2hour
export const isWithinConnectivityTimeframe = (
  updatedAt: string | number,
  isMB2: boolean,
  now?: number,
): boolean => {
  const time = now ?? Date.now();
  let lastAliveTime = isMB2 ? lastAliveTimeMB2 : lastAliveTimeMB1;
  if (Number.isInteger(updatedAt)) return (updatedAt as number) > time - lastAliveTime;
  return isoToFirebaseTimestamp(updatedAt as string) > time - lastAliveTime;
};

export const isoToFirebaseTimestamp = (isoDate: string): number => {
  const timestamp = new Date(isoDate).getTime();
  if (isNaN(timestamp)) throw new Error("invalid timestamp");
  return timestamp;
};

export function numberToCode(number: number | null): number[] {
  if (number === null) return [];
  let codesArr: number[] = [];
  let error_code = (number >>> 0).toString(2).split("").reverse().join("");
  for (let i = 0; i < error_code.length; i++) {
    if (error_code[i] === "1") codesArr.push(i + 1);
  }
  return codesArr.flat();
}

export function shorten(str: string, maxLen: number): string {
  return str.length > maxLen ? str.substring(0, maxLen) + "..." : str;
}

export function daysSinceDate(date: string | number | Date): number {
  const currentDate = new Date();
  const pastDate = new Date(date);
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((currentDate.getTime() - pastDate.getTime()) / msPerDay);
}

interface ErrorObject {
  error_code?: number;
}

export function displayError(active_error: ErrorObject | null): string {
  const error_code = active_error?.error_code;
  if (!active_error || error_code === 0 || error_code === undefined) return "";

  const code = numberToCode(error_code);
  return code.reduce((acc, curr) => (acc ? `${acc}, ${curr}` : ""), "");
}

export function downloadTextAsFile(text: string, filename = "download.txt"): void {
  // Validate input
  if (typeof text !== "string" || !text.trim()) {
    console.error("Invalid text input. Please provide a non-empty string.");
    return;
  }

  // Create a Blob with the text
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);

  // Create a temporary link element
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  // Trigger download
  document.body.appendChild(link); // Required for Firefox
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function arrayToCsv(arrayOfObjects: Record<string, any>[], cols?: string[]): string {
  if (!arrayOfObjects || arrayOfObjects.length === 0) {
    return "";
  }

  // Collect all unique keys from the objects
  const columns = cols ?? Array.from(new Set(arrayOfObjects.flatMap((obj) => Object.keys(obj))));

  // Create the header row
  const header = columns.join(",");

  // Create the rows
  const rows = arrayOfObjects.map((obj) =>
    columns.map((col) => JSON.stringify(obj[col] || "")).join(","),
  );

  // Combine header and rows into a single CSV string
  return [header, ...rows].join("\n");
}

export function flattenObject(obj: Record<string, any>): Record<string, any> {
  const flattened: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value));
    } else {
      flattened[key] = value;
    }
  });

  return flattened;
}

export function removeEmptyNullUndefined(obj: any): any {
  if (obj === null || typeof obj !== "object") return obj;

  if (Array.isArray(obj)) {
    return obj.reduce(
      (acc, curr) =>
        curr === null || curr === undefined ? acc : [...acc, removeEmptyNullUndefined(curr)],
      [],
    );
  }

  Object.keys(obj).forEach((key) => {
    if (obj[key] === undefined || obj[key] === null) delete obj[key];
    else if (typeof obj[key] === "object") {
      obj[key] = removeEmptyNullUndefined(obj[key]);
      // Only delete the property if it's an empty object, not an empty array
      if (Object.keys(obj[key]).length === 0 && !Array.isArray(obj[key])) delete obj[key];
    }
  });

  return obj;
}

export const sortNullValues = (a: any, b: any) => {
  const aIsNullish = a === null || a === undefined || Number.isNaN(a);
  const bIsNullish = b === null || b === undefined || Number.isNaN(b);

  if (aIsNullish && bIsNullish) return 0;
  else if (aIsNullish && !bIsNullish) return 1;
  else if (!aIsNullish && bIsNullish) return -1;
  else return null;
};

interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
}

type YearInput = Date | FirestoreTimestamp | string | number | null | undefined;

export function getYear(year: YearInput): number | string {
  if (!year) return "-";
  if (typeof year === "object" && "seconds" in year) {
    return new Date(year.seconds * 1000).getFullYear();
  }
  if (year instanceof Date) {
    return year.getFullYear();
  }
  if (typeof year === "string") {
    const date = new Date(year);
    return date.getFullYear();
  }
  return year;
}

//copyToClipboard function, modern
export async function copyToClipboard(text: string): Promise<string> {
  try {
    await navigator.clipboard.writeText(text);
    return text;
  } catch (e) {
    return "copied.error";
  }
}

export const MAX_IMAGE_SIZE = 1 * 1024 * 1024 * 1024; // 1GB in bytes

export function validateImageSize(file: File): boolean {
  if (!file) return false;
  return file.size <= MAX_IMAGE_SIZE;
}

export function normalizeLastSeen(lastSeen: any): Date | null {
  if (!lastSeen) return null;
  else if (lastSeen instanceof Date) return lastSeen;
  else if (typeof lastSeen === "number") return new Date(lastSeen);
  else if (lastSeen.seconds && typeof lastSeen.seconds === "number")
    return new Date(lastSeen.seconds * 1000);
  else if (lastSeen.seconds && lastSeen.nanoseconds) {
    return new Date(lastSeen.seconds * 1000 + lastSeen.nanoseconds / 1000000);
  } else return null;
}

export const statesArr: string[] = [
  "CTRL_STATE_NULL",
  "CTRL_STATE_STARTUP_EMPTYING_ST",
  "CTRL_STATE_STARTUP_TAKING_COLDWATER",
  "CTRL_STATE_STARTUP_BUILDING_PRESSURE",
  "CTLR_STATE_STARTUP_EMPTYING_FILTER",
  "CTRL_STATE_STARTUP_DUMPING_FILTER_SEDIMENT",
  "CTRL_STATE_CAL_COLDWATER_TO_FILTER",
  "CTRL_STATE_CAL_CIRCULATING",
  "CTRL_STATE_CAL_ANALYZING",
  "CTRL_STATE_CAL_CHECKING_FILTER",
  "CTRL_STATE_CAL_STORING_WATER",
  "CTRL_STATE_CAL_DUMPING_FILTER_SEDIMENT",
  "CTRL_STATE_FILTERING_PUMPING_FROM_WCU",
  "CTRL_STATE_FILTERING_CIRCULATING",
  "CTRL_STATE_FILTERING_ANALYZING",
  "CTRL_STATE_FILTERING_DISCARDING",
  "CTRL_STATE_FILTERING_STORING",
  "CTRL_STATE_FILTERING_DUMPING_INTO_MP_CONTAINER",
  "CTRL_STATE_FILTERING_RINSE_FILTER_BF",
  "CTRL_STATE_FILTERING_RINSE_FILTER_SPRAY",
  "CTRL_STATE_FILTERING_RINSE_FILTER_FLUSH",
  "CTRL_STATE_DUMPING_BOTTOM_SEDIMENT_ST",
  "CTRL_STATE_SERVING_WATER",
  "", //FIXME: EUB forgot #23
  "CTRL_STATE_SERVING_IDLE",
  "CTRL_STATE_CIRCULATING_ST",
  "CTRL_STATE_EMPTYING_ST_TO_SEWER",
  "CTRL_STATE_LEAK_DETECTED_DUMPING_WATER",
  "CTRL_STATE_WAITING",
  "CTRL_STATE_LOW_ACTIVITY_WAITING",
  "CTRL_STATE_AIRING_OUT",
  "CTRL_STATE_FILTERING_SIDESPRAY_MPCONT",
  "CTRL_STATE_FILTERING_TOPSPRAY_MPCONT",
  "CTRL_STATE_FILTERING_LOW_WATER_PUMP",
  "CTRL_STATE_STARTUP_RINSE_FILTER_BF",
  "CTRL_STATE_STARTUP_RINSE_FILTER_SPRAY",
  "CTRL_STATE_STARTUP_RINSE_FILTER_FLUSH",
  "CTRL_STATE_FILTERING_COLDWATER_ANTICLOG",
  "CTRL_STATE_CNT",
];

// To keep in sync with embedded file diagnostics.h
export enum CTRL_STATE {
  NULL = 0,

  STARTUP_EMPTYING_ST = 1,
  STARTUP_TAKING_COLDWATER = 2,
  STARTUP_BUILDING_PRESSURE = 3,
  STARTUP_EMPTYING_FILTER = 4,
  STARTUP_DUMPING_FILTER_SEDIMENT = 5,
  CAL_COLDWATER_TO_FILTER = 6,
  CAL_CIRCULATING = 7,
  CAL_ANALYZING = 8,
  CAL_STORING_WATER = 10,
  CAL_DUMPING_FILTER_SEDIMENT = 11,
  FILTERING_PUMPING_FROM_WCU = 12,
  FILTERING_CIRCULATING = 13,
  FILTERING_ANALYZING = 14,
  FILTERING_DISCARDING = 15,
  FILTERING_STORING = 16,
  FILTERING_DUMPING_INTO_MP_CONTAINER = 17,
  FILTERING_RINSE_FILTER_BF = 18,
  FILTERING_RINSE_FILTER_SPRAY = 19,
  FILTERING_RINSE_FILTER_FLUSH = 20,
  DUMPING_BOTTOM_SEDIMENT_ST = 21,
  SERVING_WATER = 22,
  SERVING_IDLE = 24,
  CIRCULATING_ST = 25,
  EMPTYING_ST_TO_SEWER = 26,
  LEAK_DETECTED_DUMPING_WATER = 27,
  WAITING = 28,
  LOW_ACTIVITY_WAITING = 29,
  AIRING_OUT = 30,
  FILTERING_SIDESPRAY_MPCONT = 31,
  FILTERING_TOPSPRAY_MPCONT = 32,
  FILTERING_LOW_WATER_PUMP = 33,
  STARTUP_RINSE_FILTER_BF = 34,
  STARTUP_RINSE_FILTER_SPRAY = 35,
  STARTUP_RINSE_FILTER_FLUSH = 36,
  FILTERING_COLDWATER_ANTICLOG = 37,
  WAITING_FOR_SERVICE = 38,
  MAINTENANCE_SPRAY = 39,
  MAINTENANCE_EMPTYING_FILTER = 40,
  MAINTENANCE_WAITING = 41,
  MAINTENANCE_DUMPING_FILTER_SEDIMENT = 42,
  NO_DATA_SAFE_STATE = 43,
  CAL_DUMPING_INTO_WCU = 44,
  CAL_PUMPING_FROM_WCU = 45,
  CAL_DISCARDING_WATER = 46,
  CAL_EMPTYING_TO_STORAGE = 47,
}
